import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, type TableActionItem } from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import { usePersistedSearchParams } from '@/preferences';
import { createBackofficeVoucher, fetchBackofficeVouchers } from './api';
import {
  CreateVoucherModal,
  VouchersFilters,
  VouchersPageActions,
  VouchersTable,
  VouchersTableHeader,
} from './components';
import type {
  BackofficeVoucher,
  CreateBackofficeVoucherInput,
  VoucherRedemptionModeFilter,
  VoucherStatus,
} from './types';

const DEFAULT_LIMIT = 50;
const VOUCHER_STATUS_VALUES = new Set<VoucherStatus>(['draft', 'active', 'paused', 'archived']);
const VOUCHER_REDEMPTION_MODE_FILTER_VALUES = new Set<VoucherRedemptionModeFilter>([
  'all',
  'single_use',
  'multi_use',
]);

function readStatuses(value: string | null): VoucherStatus[] {
  if (!value) return [];
  const statuses = value
    .split(',')
    .filter((status): status is VoucherStatus =>
      VOUCHER_STATUS_VALUES.has(status as VoucherStatus)
    );
  return Array.from(new Set(statuses));
}

function readRedemptionMode(value: string | null): VoucherRedemptionModeFilter {
  return value && VOUCHER_REDEMPTION_MODE_FILTER_VALUES.has(value as VoucherRedemptionModeFilter)
    ? (value as VoucherRedemptionModeFilter)
    : 'all';
}

export function BackofficeVouchersPage() {
  useBackofficePageTitle('Vouchers');
  const [searchParams, setSearchParams] = useSearchParams();
  const [vouchers, setVouchers] = useState<BackofficeVoucher[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatorOptions, setCreatorOptions] = useState<string[]>([]);
  const statusParam = searchParams.get('status');
  const selectedStatuses = useMemo(() => readStatuses(statusParam), [statusParam]);
  const redemptionMode = readRedemptionMode(searchParams.get('redemptionMode'));
  const createdByEmail = searchParams.get('createdByEmail') ?? '';

  const requestParams = useMemo(() => {
    const params = new URLSearchParams({
      limit: String(DEFAULT_LIMIT),
      offset: '0',
      sortBy: 'createdAt',
      sortDirection: 'desc',
    });
    if (selectedStatuses.length > 0) {
      params.set('status', selectedStatuses.join(','));
    }
    if (redemptionMode !== 'all') {
      params.set('redemptionMode', redemptionMode);
    }
    if (createdByEmail) {
      params.set('createdByEmail', createdByEmail);
    }
    return params;
  }, [createdByEmail, redemptionMode, selectedStatuses]);

  const loadVouchers = useCallback(
    async (signal?: AbortSignal) => {
      const response = await fetchBackofficeVouchers(new URLSearchParams(requestParams), signal);
      setVouchers(response.vouchers);
      setTotal(response.page.total);
      setCreatorOptions((current) => {
        const next = new Set(current);
        for (const voucher of response.vouchers) {
          if (voucher.createdByEmail) next.add(voucher.createdByEmail);
        }
        return [...next].sort((left, right) => left.localeCompare(right));
      });
    },
    [requestParams]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    loadVouchers(controller.signal)
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : 'Failed to load vouchers');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [loadVouchers]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      await loadVouchers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to refresh vouchers');
    } finally {
      setRefreshing(false);
    }
  }, [loadVouchers]);

  async function handleCreate(input: CreateBackofficeVoucherInput) {
    setCreating(true);
    setCreateError(null);
    try {
      await createBackofficeVoucher(input);
      setCreateModalOpen(false);
      await loadVouchers();
    } catch (caught) {
      setCreateError(caught instanceof Error ? caught.message : 'Failed to create voucher');
    } finally {
      setCreating(false);
    }
  }

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: loading,
        isLoading: refreshing,
      },
    ],
    [handleRefresh, loading, refreshing]
  );

  usePersistedSearchParams({
    scope: 'list:vouchers',
    keys: ['status', 'redemptionMode', 'createdByEmail'],
    searchParams,
    setSearchParams,
  });

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader
          actions={
            <VouchersPageActions
              onCreateVoucher={() => {
                setCreateError(null);
                setCreateModalOpen(true);
              }}
            />
          }
        >
          Vouchers
        </PageHeader>

        <section className="rounded-xl border border-neutral-200 bg-white">
          <VouchersTableHeader loading={loading} total={total} actions={tableActions} />
          <VouchersFilters
            selectedStatuses={selectedStatuses}
            redemptionMode={redemptionMode}
            createdByEmail={createdByEmail}
            creatorOptions={creatorOptions}
            onStatusesChange={(statuses) =>
              updateParams({ status: statuses.length > 0 ? statuses.join(',') : null })
            }
            onRedemptionModeChange={(nextRedemptionMode) =>
              updateParams({
                redemptionMode: nextRedemptionMode === 'all' ? null : nextRedemptionMode,
              })
            }
            onCreatedByEmailChange={(email) => updateParams({ createdByEmail: email })}
          />

          {loading ? (
            <div className="px-5 py-16 text-center text-body text-neutral-500">
              Loading vouchers
            </div>
          ) : error ? (
            <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
          ) : vouchers.length > 0 ? (
            <VouchersTable vouchers={vouchers} />
          ) : (
            <div className="px-5 py-16 text-center text-body text-neutral-500">
              No vouchers created yet.
            </div>
          )}
        </section>

        {createModalOpen ? (
          <CreateVoucherModal
            submitting={creating}
            error={createError}
            onClose={() => {
              if (!creating) setCreateModalOpen(false);
            }}
            onSubmit={handleCreate}
          />
        ) : null}
      </div>
    </main>
  );
}
